import React, { useState, useEffect } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';

const Achievements = () => {
  const [isIntroVisible, setIsIntroVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    const section = document.getElementById('achievements');
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.id === 'achievements-intro') {
              setIsIntroVisible(true);
            } else if (entry.target.dataset.index !== undefined) {
              const index = Number(entry.target.dataset.index);
              setVisibleItems((prev) => {
                if (!prev.includes(index)) return [...prev, index];
                return prev;
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(document.getElementById('achievements-intro'));
    const items = document.querySelectorAll('#achievements .achievement-item');
    items.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="achievements" className="py-20 section-bg-3 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Intro with fade/slide animation */}
        <div
          id="achievements-intro"
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Achievements
            </span>
          </h2>
        </div>

        {/* Achievement cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {portfolioConfig.achievements.map((achievement, index) => (
            <div
              key={index}
              data-index={index}
              className={`achievement-item group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-cyan-400/50 transition-all duration-700 transform ${
                visibleItems.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              } hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/10`}
              style={{
                transitionDelay: `${index * 150}ms`
              }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-2xl group-hover:animate-bounce">
                  <Icon name={achievement.icon} className="text-black" />
                </div>

                <div className="flex-1">
                  <div className='flex align-center gap-2'>
                    <span className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">{achievement.title}</span>
                    {achievement.link && (
                      <a
                        href={achievement.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors group/link"
                      >
                        <Icon
                          name="external-link"
                          className="group-hover/link:translate-x-1 transition-transform"
                        />
                      </a>
                    )}
                  </div>
                  <p className="text-gray-300 mt-2">
                    {achievement.description}
                  </p>
                </div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
            </div>
          ))}
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-1/3 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/3 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        </div>
      </div>
    </section>
  );
};

export default Achievements;